/**
 * How many weekly sets each muscle gets from the plan, and what removing one
 * exercise does to that — the line the remove toast states, and its warning
 * below the minimum effective dose (`D182`).
 *
 * ── FRACTIONAL, ON PURPOSE ────────────────────────────────────────────────
 * A set counts 1 for each TARGET muscle of its exercise and 0.5 for each
 * SYNERGIST, the evidence's own way of counting the floor it sets. The
 * Progress screen counts direct sets only, so the two numbers differ for the
 * same week. That is deliberate (`D182`); neither is to be "fixed" to match.
 *
 * ── ABSENT MUSCLES ARE AN OLDER SERVER ────────────────────────────────────
 * `exercise.muscles` is missing from a server older than the field. The
 * exercise then counts its `primaryMuscle` as its only target — never as
 * working nothing, which would drop it out of every total.
 *
 * Pure: no React, no messages. The copy is the app's.
 */
import { FLOORS, } from "@repo/contract/training";
const WEIGHT = { synergist: 0.5, target: 1 };
/** Every muscle one item works, with the sets it adds to each. */
function sharesOf(item) {
    const sets = item.targetSets ?? 0;
    const { muscles } = item.exercise;
    // An empty list is read like an absent one: the exercise still has a
    // primary muscle, and counting nothing would drop it from every total.
    if (muscles === undefined || muscles.length === 0) {
        const { id, name } = item.exercise.primaryMuscle;
        return [{ id, name, sets }];
    }
    return muscles.map(({ id, name, role }) => ({
        id,
        name,
        sets: sets * WEIGHT[role],
    }));
}
/** To the nearest half set — the only precision the count has. */
const toHalf = (sets) => Math.round(sets * 2) / 2;
/** Fractional weekly sets per muscle over the whole plan. */
export function weeklyMuscleVolume(plan) {
    const raw = new Map();
    for (const session of plan.sessions) {
        for (const item of session.items) {
            for (const share of sharesOf(item)) {
                const entry = raw.get(share.id);
                if (entry) {
                    entry.sets += share.sets;
                }
                else {
                    raw.set(share.id, { name: share.name, sets: share.sets });
                }
            }
        }
    }
    for (const entry of raw.values()) {
        entry.sets = toHalf(entry.sets);
    }
    return raw;
}
/**
 * What removing one item does: only the muscles whose volume changes, in the
 * item's own muscle order (targets first). Empty when the item is not in the
 * plan or carries no sets.
 */
export function volumeAfterRemove(plan, itemId) {
    const item = plan.sessions
        .flatMap((session) => session.items)
        .find((candidate) => candidate.itemId === itemId);
    if (!item) {
        return [];
    }
    const removed = new Map();
    for (const share of sharesOf(item)) {
        const seen = removed.get(share.id);
        removed.set(share.id, seen ? { ...seen, sets: seen.sets + share.sets } : share);
    }
    const volume = weeklyMuscleVolume(plan);
    const floor = FLOORS.minEffectiveWeeklySetsPerMuscle;
    const changes = [];
    for (const share of removed.values()) {
        const before = volume.get(share.id)?.sets ?? 0;
        const after = toHalf(Math.max(0, before - share.sets));
        if (after === before) {
            continue;
        }
        changes.push({
            after,
            before,
            belowFloor: before >= floor && after < floor,
            muscleId: share.id,
            name: volume.get(share.id)?.name ?? share.name,
        });
    }
    return changes;
}
//# sourceMappingURL=coverage.js.map